import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useBackends, useDeleteBackend } from "@/api/backends";
import type { Backend } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { BackendEditDialog } from "./BackendEditDialog";
import { BACKEND_KIND_META } from "./credentialSchemas";

const KIND_TONE: Record<Backend["kind"], string> = {
  grafana: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40",
  azure: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40",
  datadog: "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/40",
  dynatrace: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
  aws: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",
  splunk: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/40",
  adx: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40",
  solarwinds: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40",
};

export function BackendsListPage() {
  const backends = useBackends();
  const del = useDeleteBackend();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Backend | null>(null);
  const rows = backends.data ?? [];
  const permissions = usePermissions();

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Backends</h2>
          <p className="text-sm text-muted-foreground">
            Observability backends DemoSim can push synthetic telemetry into.
          </p>
        </div>
        <Button
          disabled={!permissions.canManageBackends}
          title={permissions.backendReason ?? "Insufficient permission"}
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus size={14} /> New backend
        </Button>
      </header>

      {!permissions.canManageBackends && (
        <p className="text-xs text-muted-foreground">
          {permissions.backendReason ?? "Only admins can manage backends."}
        </p>
      )}

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>ID</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((b) => (
              <TableRow key={b.id}>
                <TableCell className="font-medium text-foreground">{b.name}</TableCell>
                <TableCell>
                  <span
                    className={cn(
                      "inline-flex items-center h-5 px-2 rounded-full text-[10px] font-medium border uppercase tracking-wide",
                      KIND_TONE[b.kind],
                    )}
                  >
                    {BACKEND_KIND_META[b.kind]?.label ?? b.kind}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{b.id}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={!permissions.canManageBackends}
                    title={permissions.backendReason ?? "Insufficient permission"}
                    onClick={() => {
                      setEditing(b);
                      setDialogOpen(true);
                    }}
                    aria-label={`Edit ${b.name}`}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    disabled={!permissions.canManageBackends}
                    title={permissions.backendReason ?? "Insufficient permission"}
                    onClick={() => {
                      if (confirm(`Delete ${b.name}?`)) del.mutate(b.id);
                    }}
                    aria-label={`Delete ${b.name}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-12">
                  No backends yet. Click <span className="font-semibold">New backend</span> to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <BackendEditDialog open={dialogOpen} onOpenChange={setDialogOpen} existing={editing} />
    </div>
  );
}
